import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useReadyAlert, requestNotifyPermission } from "@/hooks/useReadyAlert";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { MenuGrid } from "@/components/customer/MenuGrid";
import { CartDrawer } from "@/components/customer/CartDrawer";
import { StampCard } from "@/components/customer/StampCard";
import { ReviewQR } from "@/components/ReviewQR";
import { OpeningBadge, usePublicInfo } from "@/components/OpeningBadge";
import { OrderTracker } from "@/components/customer/OrderTracker";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { UtensilsCrossed, Stamp, Receipt, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function CustomerHome() {
  const { user, setUser } = useAuth();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState({});
  const [table, setTable] = useState(() => {
    const fromUrl = parseInt(searchParams.get("table"), 10);
    return fromUrl >= 1 && fromUrl <= 12 ? fromUrl : null;
  });
  const [tab, setTab] = useState("menu");
  const publicInfo = usePublicInfo();
  const isOpen = publicInfo ? publicInfo.open_now : true;
  const goToOrders = useCallback(() => setTab("orders"), []);
  useReadyAlert(goToOrders);

  useEffect(() => {
    const fromUrl = parseInt(searchParams.get("table"), 10);
    if (fromUrl >= 1 && fromUrl <= 12) {
      setTable(fromUrl);
    }
  }, [searchParams]);

  const loadMenu = () => api.get("/menu").then((r) => setMenu(r.data)).catch(() => {});
  useEffect(() => { loadMenu(); }, []);

  const cartCount = useMemo(() => Object.values(cart).reduce((a, b) => a + b, 0), [cart]);

  const addToCart = (item) => {
    setCart((c) => ({ ...c, [item.item_id]: (c[item.item_id] || 0) + 1 }));
    toast.success(`${item.name_it} — ${t("added")}`, { duration: 1200 });
  };

  const changeQty = (itemId, delta) => {
    setCart((c) => {
      const next = { ...c, [itemId]: (c[itemId] || 0) + delta };
      if (next[itemId] <= 0) delete next[itemId];
      return next;
    });
  };

  const submitOrder = async (note, people) => {
    if (!isOpen) {
      toast.error(t("closed_order_blocked"));
      throw new Error("closed");
    }
    if (!table) {
      toast.error(t("scan_qr_hint"));
      throw new Error("no table");
    }
    const items = Object.entries(cart).map(([item_id, quantity]) => ({ item_id, quantity }));
    await api.post("/orders", { table_number: table, note, items, people });
    requestNotifyPermission();
    setCart({});
    toast.success(t("order_placed"));
    setTab("orders");
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="customer-home">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-5">
        <div
          data-testid="table-indicator"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#4A2C2A] bg-[#F6F1E9] border border-[#E8DFD5] rounded-full px-4 py-2"
        >
          <MapPin className="w-4 h-4 text-[#D97706]" />
          {table ? `${t("table")} ${table}` : t("scan_qr_hint")}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-5">
          <TabsList className="bg-[#F6F1E9] border border-[#E8DFD5]">
            <TabsTrigger data-testid="tab-menu" value="menu" className="gap-1.5">
              <UtensilsCrossed className="w-4 h-4" /> {t("nav_menu")}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-loyalty" value="stamps" className="gap-1.5 notranslate" translate="no">
              <Stamp className="w-4 h-4" /> {t("nav_loyalty")}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-orders" value="orders" className="gap-1.5">
              <Receipt className="w-4 h-4" /> {t("nav_orders")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="menu" className="mt-6">
            {!isOpen && (
              <div data-testid="closed-banner" className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-sm font-semibold">
                {t("closed_order_blocked")}
              </div>
            )}
            <MenuGrid menu={menu} onAdd={addToCart} />
            <div className="mt-8 max-w-lg space-y-6">
              <OpeningBadge info={publicInfo} />
              <ReviewQR />
            </div>
          </TabsContent>
          <TabsContent value="stamps" className="mt-6">
            <StampCard user={user} onUserUpdate={setUser} />
          </TabsContent>
          <TabsContent value="orders" className="mt-6">
            <OrderTracker onClosed={() => { setCart({}); setTab("menu"); window.scrollTo(0, 0); }} />
          </TabsContent>
        </Tabs>
      </div>

      <CartDrawer
        menu={menu}
        cart={cart}
        cartCount={cartCount}
        table={table}
        onChangeQty={changeQty}
        onSubmit={submitOrder}
      />
    </div>
  );
}
